import { Tooltip } from '@/ui/rapid-ui/tooltip'
import type { CandidateModel } from '@/ui/views/shared/candidate'
import { format, formatDistanceToNow } from 'date-fns'
import { AlertTriangleIcon, CalendarCheckIcon, CalendarXIcon, HourglassIcon, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export type InterviewBadgeState = 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show' | 'expired'

const STATE_STYLES: Record<InterviewBadgeState, { className: string; Icon: LucideIcon }> = {
	pending: { className: 'bg-gray-100 text-gray-600', Icon: HourglassIcon },
	confirmed: { className: 'bg-green-100 text-green-700', Icon: CalendarCheckIcon },
	completed: { className: 'bg-green-600 text-white', Icon: CalendarCheckIcon },
	cancelled: { className: 'bg-amber-100 text-amber-700', Icon: CalendarXIcon },
	no_show: { className: 'bg-red-100 text-red-700', Icon: CalendarXIcon },
	expired: { className: 'bg-gray-100 text-gray-600', Icon: AlertTriangleIcon },
}

function formatCountdown(target: Date, now: Date): string {
	const diffMs = target.getTime() - now.getTime()
	const diffMinutes = Math.round(diffMs / 60_000)

	if (diffMinutes <= 0) {
		return '0m'
	}
	if (diffMinutes < 60) {
		return `${diffMinutes}m`
	}

	const diffHours = Math.round(diffMinutes / 60)
	if (diffHours < 24) {
		return `${diffHours}h`
	}

	const diffDays = Math.round(diffHours / 24)
	return `${diffDays}d`
}

export function InterviewBookingBadge({ candidate }: { candidate: CandidateModel }) {
	const { t } = useTranslation('common')
	const now = new Date()
	const state = candidate.interviewBadgeState ?? null

	if (state === null) {
		return null
	}

	const { className, Icon } = STATE_STYLES[state]
	const scheduledAt = state === 'confirmed' ? candidate.interviewBooking?.scheduledAt : undefined
	const countdown = scheduledAt ? formatCountdown(scheduledAt, now) : null

	// Doubles as the accessible name: the icon is aria-hidden and most states have
	// no visible text, so without this a screen reader gets nothing from the badge.
	// The pill itself only has room for the compact countdown ("2h"), which means
	// nothing on its own — the tooltip spells it out with the exact date and a
	// long-form duration ("in 4 days") instead of repeating the compact form.
	const label = scheduledAt
		? t('interviewBadge.confirmedWithCountdown', {
			datetime: format(scheduledAt, 'PPp'),
			duration: formatDistanceToNow(scheduledAt),
		})
		: t(`interviewBadge.${state}`)

	return (
		<Tooltip content={label}>
			<span
				aria-label={label}
				className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${className}`}
			>
				<Icon className="size-3" aria-hidden="true" />
				{countdown}
			</span>
		</Tooltip>
	)
}
